'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  currentLanguage: string;
}

const HeroSection = ({ currentLanguage }: HeroSectionProps) => {
  const content = {
    en: {
      badge: 'AI-Powered Digitalization',
      title: 'Transform to Activity-Based Emission Tracking',
      subtitle: 'From spend-based estimates to precise, real-time monitoring',
      description:
        'Leverage AI and IoT integration to transition from traditional spend-based carbon accounting to accurate activity-based tracking. Real-time emission monitoring, predictive analytics, and MRV validation—all in one unified platform.',
      cta: 'Start Calculating',
      secondaryCta: 'View Industry Solutions',
      features: [
        'AI-powered emission algorithms',
        'Real-time IoT integration',
        'Scope 1, 2 & 3 calculation',
        'MRV validation system',
      ],
    },
    hi: {
      badge: 'AI-संचालित डिजिटलीकरण',
      title: 'गतिविधि-आधारित उत्सर्जन ट्रैकिंग में परिवर्तन',
      subtitle: 'खर्च-आधारित अनुमानों से सटीक, वास्तविक समय निगरानी तक',
      description:
        'पारंपरिक खर्च-आधारित कार्बन लेखांकन से सटीक गतिविधि-आधारित ट्रैकिंग में परिवर्तन के लिए AI और IoT एकीकरण का लाभ उठाएं। वास्तविक समय उत्सर्जन निगरानी, पूर्वानुमान विश्लेषण, और MRV सत्यापन—सभी एक एकीकृत मंच में।',
      cta: 'गणना शुरू करें',
      secondaryCta: 'उद्योग समाधान देखें',
      features: [
        'AI-संचालित उत्सर्जन एल्गोरिदम',
        'वास्तविक समय IoT एकीकरण',
        'Scope 1, 2 और 3 गणना',
        'MRV सत्यापन प्रणाली',
      ],
    },
    ar: {
      badge: 'الرقمنة المدعومة بالذكاء الاصطناعي',
      title: 'التحول إلى تتبع الانبعاثات القائم على النشاط',
      subtitle: 'من التقديرات القائمة على الإنفاق إلى المراقبة الدقيقة في الوقت الفعلي',
      description:
        'استفد من الذكاء الاصطناعي وتكامل إنترنت الأشياء للانتقال من محاسبة الكربون التقليدية القائمة على الإنفاق إلى التتبع الدقيق القائم على النشاط. مراقبة الانبعاثات في الوقت الفعلي والتحليلات التنبؤية والتحقق من MRV - كل ذلك في منصة واحدة موحدة.',
      cta: 'ابدأ الحساب',
      secondaryCta: 'عرض حلول الصناعة',
      features: [
        'خوارزميات الانبعاثات بالذكاء الاصطناعي',
        'تكامل إنترنت الأشياء في الوقت الفعلي',
        'حساب النطاق 1 و 2 و 3',
        'نظام التحقق من MRV',
      ],
    },
  };

  const t = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="relative bg-gradient-to-br from-primary/5 via-info/5 to-secondary/5 pt-24 pb-20">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
            <Icon name="CpuChipIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">{t.badge}</span>
          </div>

          <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-text-primary mb-6 leading-tight">
            {t.title}
          </h1>

          <p className="text-xl text-primary font-semibold mb-4">{t.subtitle}</p>

          <p className="text-lg text-text-secondary leading-relaxed mb-8 max-w-3xl mx-auto">
            {t.description}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link
              href="#emission-calculator"
              className="w-full sm:w-auto px-8 py-4 bg-conversion text-conversion-foreground font-heading font-semibold text-base rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic transform hover:scale-102"
            >
              {t.cta}
            </Link>
            <Link
              href="#industry-modules"
              className="w-full sm:w-auto px-8 py-4 bg-card text-text-primary border-2 border-primary font-heading font-semibold text-base rounded-lg hover:bg-primary hover:text-primary-foreground shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic"
            >
              {t.secondaryCta}
            </Link>
          </div>

          {/* Key Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.features.map((feature, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-lg p-4 hover:shadow-natural transition-all duration-300"
              >
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success mt-1 flex-shrink-0" />
                  <p className="text-sm font-medium text-text-primary text-left">{feature}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Platform Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          {[
            { value: '99.8%', label: 'Calculation Accuracy' },
            { value: '1000+', label: 'Connected IoT Devices' },
            { value: 'Real-time', label: 'Data Processing' },
            { value: '24/7', label: 'AI Monitoring' },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-text-secondary">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;